import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ModelsTransformUtil } from '@ddt-web/common';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { ComponentData } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { keys } from 'highcharts';
import * as cloneDeep from 'lodash/cloneDeep';

import { ApplicationConstants } from '../../config';
import { FamilyListDropdown, ModelDropdown, ModelsLevelCategories } from '../../models';

@Component({
	selector: 'mb-mini-compare-dropdown',
	templateUrl: './mini-compare-dropdown.component.html',
	styleUrls: ['./mini-compare-dropdown.component.scss']
})
export class MiniCompareDropdownComponent implements OnInit {
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_ADD_TO_COMPARE_MODEL) public readonly enableAddToCompareModel: boolean = false;

	@Input() public data: ComponentData;
	@Input() public familiesData: ModelsLevelCategories;
	@Input() public selectedModels: ModelDropdown[] = [];
	@Output() public getSelectedFamilies: EventEmitter<ModelDropdown[]> = new EventEmitter<ModelDropdown[]>();
	@Output() public familyListDropdown: EventEmitter<FamilyListDropdown[]> = new EventEmitter<FamilyListDropdown[]>();

	public familyList: FamilyListDropdown[] = [];
	public ApplicationConstants: ApplicationConstants = ApplicationConstants;

	constructor(private tealiumAnalyticsService: TealiumAnalyticsService, private contentService: ContentService) { }

	public ngOnInit(): void {
		const families = (this.data?.getTabById('me.dropdown.family.list')?.tableData?.[0] as any)?.rows ?? [];
		this.generateFamiliesData(families);
	}

	public onSelectedFamilyChange(selectedFamily: FamilyListDropdown, index: number): void {
		const selectedModels = cloneDeep(this.selectedModels);
		if (selectedModels[index].familyId !== selectedFamily?.familyId ||
			selectedModels[index].groupId !== selectedFamily?.groupId) {
			//we are setting model Id as null on family change, so that consuming component can set model to default value on family change
			selectedModels[index] = {
				...this.selectedModels[index],
				modelId: null,
				familyName: selectedFamily.familyName,
				familyId: selectedFamily.familyId,
				groupId: selectedFamily.groupId
			};
			const eventName = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `:mini compare dropdown:${selectedFamily.familyName}`;
			this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(eventName, { toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE }));
			this.selectedModels = cloneDeep(selectedModels);
			this.getSelectedFamilies.emit(this.selectedModels);
		}
	}

	private generateFamiliesData(families): void {
		const sortParams = {
			asOfDate: 'asOfDate.formattedValue',
			displayOrder: 'displayOrder.formattedValue',
			subType: 'subType.formattedValue',
			tradeFrequency: 'tradeFrequency.formattedValue'
		};

		const familyGroupPerCategory = {};
		this.familiesData.categories.forEach((category: string) => {
			//filter and sort all  families per category
			const familyIds = keys(this.familiesData.familyModelsPerCategory[category]) || [];
			const familyGroup = families.filter(family => familyIds.includes(family.familyId?.formattedValue));
			familyGroupPerCategory[category] = ModelsTransformUtil.sortFamiliesWithTradeDateOrFrequency(familyGroup, category, sortParams, false);
		});

		this.rearrangeFamilyData(familyGroupPerCategory);
	}

	private rearrangeFamilyData(familyGroupPerCategory): void {
		if (keys(familyGroupPerCategory).length > 1) {
			// find out custom category and generate custom family group
			const customCategory = keys(familyGroupPerCategory).find(category => category !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP);
			const customGroup = familyGroupPerCategory[customCategory]?.map(family => ({
				familyId: family?.familyId?.formattedValue,
				familyName: family.modelExternalName?.formattedValue || family?.description?.formattedValue,
				groupId: customCategory,
				disabled: false
			}));

			const blkGroup = familyGroupPerCategory[SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP]?.map(family => ({
				familyId: family?.familyId?.formattedValue,
				familyName: family?.description?.formattedValue,
				groupId: SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP,
				disabled: false
			}));

			this.familyList = [
				{
					familyId: '',
					familyName: this.contentService.get('CUSTOM_MODELS_TITLE', 'Your Custom Models'),
					disabled: true
				},
				...customGroup,
				{
					familyId: '',
					familyName: this.contentService.get('BLK_MODELS_TITLE', 'BlackRock Models'),
					disabled: true
				},
				...blkGroup
			];

		} else {
			//find family category value and assign the groupId accordingly
			const familyCategory = keys(familyGroupPerCategory)?.[0];
			this.familyList = familyGroupPerCategory[familyCategory].map(family => ({
				familyId: family?.familyId?.formattedValue,
				familyName: familyCategory !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP && family?.modelExternalName?.formattedValue
					? family.modelExternalName.formattedValue : family?.description?.formattedValue,
				groupId: familyCategory,
				disabled: false
			}));
		}
		this.familyListDropdown.emit(this.familyList);
	}
}
